/**
 * Whiteboard CRUD — Phase 5c replacement.
 * Delegates to /api/charts/* endpoints on dash-api.
 */

import {
    addWhiteboardLinkToList,
    buildChartLink,
    normalizeWhiteboardLinks,
    removeChartLinkToTarget,
    removeWhiteboardLinkFromList,
    WHITEBOARD_LINK_TYPES,
} from '../../features/whiteboards/utils/whiteboardLinks';

const BASE = process.env.REACT_APP_AWP_API_URL || 'https://awp.minsky.ai';

function authHeaders() {
    const token = localStorage.getItem('access_token');
    return token
        ? {'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json'}
        : {'Content-Type': 'application/json'};
}

async function apiFetch(path, opts = {}) {
    const resp = await fetch(`${BASE}${path}`, {headers: authHeaders(), ...opts});
    if (resp.status === 404) return null;
    if (!resp.ok) throw new Error(`${opts.method || 'GET'} ${path} → ${resp.status}`);
    if (resp.status === 204) return null;
    return resp.json();
}

export const normalizeWhiteboardTitle = (title) => {
    const normalized = typeof title === 'string' ? title.trim() : '';
    return normalized || 'Untitled';
};

function looksLikeIsoDateTime(str) {
    return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(str);
}

function resolveTitleFromRow(row) {
    const raw = row?.title ?? row?.name;
    if (raw == null) return 'Untitled';
    if (typeof raw === 'object') return 'Untitled';
    const str = String(raw).trim();
    if (!str || looksLikeIsoDateTime(str)) return 'Untitled';
    return normalizeWhiteboardTitle(str);
}

function normalizeWhiteboardRow(row) {
    if (!row || typeof row !== 'object') return row;
    return {...row, title: resolveTitleFromRow(row)};
}

export const subscribeToWhiteboards = (onChange, onError) => {
    let cancelled = false;
    apiFetch('/api/charts')
        .then((data) => {
            if (cancelled) return;
            const rows = Array.isArray(data) ? data.map(normalizeWhiteboardRow) : [];
            onChange?.(rows);
        })
        .catch((err) => { if (!cancelled) onError?.(err); });
    return () => { cancelled = true; };
};

/** @type {Map<string, { listeners: Set<{ onChange?: Function, onError?: Function }>, promise?: Promise<void>, data?: object|null, error?: Error|null, loaded: boolean }>} */
const whiteboardByIdCache = new Map();

/** @internal test helper */
export function resetWhiteboardByIdCacheForTests() {
    whiteboardByIdCache.clear();
}

function patchWhiteboardCache(whiteboardId, patch) {
    const entry = whiteboardByIdCache.get(whiteboardId);
    if (!entry?.loaded || !entry.data) {
        return;
    }
    entry.data = normalizeWhiteboardRow({...entry.data, ...patch});
    for (const listener of entry.listeners) {
        listener.onChange?.(entry.data);
    }
}

export const subscribeToWhiteboard = (whiteboardId, onChange, onError) => {
    if (!whiteboardId) {
        Promise.resolve().then(() => onChange?.(null));
        return () => {};
    }

    let entry = whiteboardByIdCache.get(whiteboardId);
    if (!entry) {
        entry = {listeners: new Set(), loaded: false};
        whiteboardByIdCache.set(whiteboardId, entry);

        entry.promise = apiFetch(`/api/charts/${encodeURIComponent(whiteboardId)}`)
            .then((data) => {
                entry.data = data ? normalizeWhiteboardRow(data) : data;
                entry.error = null;
                entry.loaded = true;
                for (const listener of entry.listeners) {
                    listener.onChange?.(entry.data);
                }
            })
            .catch((err) => {
                entry.error = err;
                entry.loaded = true;
                for (const listener of entry.listeners) {
                    listener.onError?.(err);
                }
            });
    }

    const listener = {onChange, onError};
    entry.listeners.add(listener);

    if (entry.loaded) {
        Promise.resolve().then(() => {
            if (entry.error) onError?.(entry.error);
            else onChange?.(entry.data);
        });
    }

    return () => {
        entry.listeners.delete(listener);
        if (entry.listeners.size === 0) {
            whiteboardByIdCache.delete(whiteboardId);
        }
    };
};

export const getWhiteboardById = async (whiteboardId) => {
    return apiFetch(`/api/charts/${encodeURIComponent(whiteboardId)}`);
};

export const buildWhiteboardCreatePayload = ({user, title = 'Untitled', userHandle = null}) => ({
    title: normalizeWhiteboardTitle(title),
    userId: user.uid,
    userDisplayName: user.displayName || 'Anonymous',
    userEmail: user.email || null,
    userHandle,
});

export const createWhiteboard = async ({user, title = 'Untitled'}) => {
    if (!user?.uid) throw new Error('Authenticated user is required to create a whiteboard');
    const payload = buildWhiteboardCreatePayload({user, title});
    const data = await apiFetch('/api/charts', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
    return {whiteboardId: data?.id};
};

export const updateWhiteboardTitle = async ({whiteboardId, title}) => {
    if (!whiteboardId) throw new Error('whiteboardId is required to rename a whiteboard');
    const normalizedTitle = normalizeWhiteboardTitle(title);
    await apiFetch(`/api/charts/${encodeURIComponent(whiteboardId)}`, {
        method: 'PATCH',
        body: JSON.stringify({title: normalizedTitle}),
    });
    patchWhiteboardCache(whiteboardId, {title: normalizedTitle});
};

export const updateWhiteboardLinks = async ({whiteboardId, links}) => {
    if (!whiteboardId) throw new Error('whiteboardId is required to update links');
    await apiFetch(`/api/charts/${encodeURIComponent(whiteboardId)}`, {
        method: 'PATCH',
        body: JSON.stringify({
            links: Array.isArray(links) ? links : [],
            associatedProduct: null,
        }),
    });
    return links;
};

export const addWhiteboardLink = async ({whiteboardId, currentLinks, link}) => {
    if (!whiteboardId) throw new Error('whiteboardId is required to add a link');
    if (!link?.targetId || !link?.type) throw new Error('A valid link is required');

    const existing = Array.isArray(currentLinks) ? currentLinks : [];
    const duplicate = existing.some(
        (entry) => entry.type === link.type && entry.targetId === link.targetId,
    );
    const nextLinks = duplicate ? existing : [...existing, link];
    await updateWhiteboardLinks({whiteboardId, links: nextLinks});
    return nextLinks;
};

export const addWhiteboardChartLinkPair = async ({
    sourceWhiteboardId,
    sourceTitle,
    currentLinks,
    targetChart,
    user,
}) => {
    if (!sourceWhiteboardId) throw new Error('sourceWhiteboardId is required to add a chart link');
    if (!targetChart?.id) throw new Error('A valid target chart is required');
    if (sourceWhiteboardId === targetChart.id) {
        throw new Error('A chart cannot link to itself.');
    }

    const link = buildChartLink(targetChart, user);
    if (!link) throw new Error('A valid chart link is required');

    const existing = Array.isArray(currentLinks) ? currentLinks : [];
    if (existing.some((entry) => entry.type === link.type && entry.targetId === link.targetId)) {
        return existing;
    }

    const reciprocal = buildChartLink({id: sourceWhiteboardId, title: sourceTitle}, user);
    if (!reciprocal) throw new Error('Failed to build reciprocal chart link.');

    const targetDoc = await getWhiteboardById(targetChart.id);
    if (!targetDoc) throw new Error('Chart not found.');

    const targetLinks = normalizeWhiteboardLinks(targetDoc);
    const nextTargetLinks = addWhiteboardLinkToList(targetLinks, reciprocal);
    await updateWhiteboardLinks({whiteboardId: targetChart.id, links: nextTargetLinks});

    try {
        const nextSourceLinks = addWhiteboardLinkToList(existing, link);
        await updateWhiteboardLinks({whiteboardId: sourceWhiteboardId, links: nextSourceLinks});
        return nextSourceLinks;
    } catch (error) {
        await updateWhiteboardLinks({whiteboardId: targetChart.id, links: targetLinks});
        throw error;
    }
};

export const removeWhiteboardLink = async ({whiteboardId, currentLinks, linkId}) => {
    if (!whiteboardId) throw new Error('whiteboardId is required to remove a link');
    if (!linkId) throw new Error('linkId is required to remove a link');

    const existing = Array.isArray(currentLinks) ? currentLinks : [];
    const removed = existing.find((link) => link.id === linkId);
    const nextLinks = removeWhiteboardLinkFromList(existing, linkId);
    await updateWhiteboardLinks({whiteboardId, links: nextLinks});

    if (removed?.type === WHITEBOARD_LINK_TYPES.CHART && removed.targetId) {
        const targetDoc = await getWhiteboardById(removed.targetId);
        if (targetDoc) {
            const targetLinks = normalizeWhiteboardLinks(targetDoc);
            const nextTargetLinks = removeChartLinkToTarget(targetLinks, whiteboardId);
            if (nextTargetLinks.length !== targetLinks.length) {
                await updateWhiteboardLinks({whiteboardId: removed.targetId, links: nextTargetLinks});
            }
        }
    }

    return nextLinks;
};

/** @deprecated Use updateWhiteboardLinks instead. */
export const updateWhiteboardAssociatedProduct = async ({whiteboardId, associatedProduct}) => {
    if (!whiteboardId) throw new Error('whiteboardId is required to update associated product');
    await apiFetch(`/api/charts/${encodeURIComponent(whiteboardId)}`, {
        method: 'PATCH',
        body: JSON.stringify({associatedProduct: associatedProduct ?? null}),
    });
};

/** Fetch ICRs linked to a chart via icr_associations (item_type=whiteboard). */
export const fetchChartRequirements = async (whiteboardId) => {
    if (!whiteboardId) return [];
    const data = await apiFetch(`/api/charts/${encodeURIComponent(whiteboardId)}/requirements`);
    return Array.isArray(data) ? data : [];
};

export const deleteWhiteboard = async ({whiteboardId}) => {
    if (!whiteboardId) throw new Error('whiteboardId is required to delete a whiteboard');
    await apiFetch(`/api/charts/${encodeURIComponent(whiteboardId)}`, {method: 'DELETE'});
};

export const cloneWhiteboard = async ({whiteboardId, user}) => {
    if (!whiteboardId) throw new Error('whiteboardId is required to clone a whiteboard');
    if (!user?.uid) throw new Error('Authenticated user is required to clone a whiteboard');
    const data = await apiFetch(`/api/charts/${encodeURIComponent(whiteboardId)}/clone`, {
        method: 'POST',
        body: JSON.stringify({
            userId: user.uid,
            userDisplayName: user.displayName || 'Anonymous',
            userEmail: user.email || null,
        }),
    });
    return {whiteboardId: data?.id};
};
